import { acceptHMRUpdate, defineStore } from 'pinia'
import { clone, throttle } from 'lodash-es'
import * as Sentry from '@sentry/vue'

import { useSettingsGeneralStore } from './settings-general'
import { useUserStore } from './user'

import { getCurrencyIdFromAsset } from '@/helpers/assets'
import { HttpError } from '@/helpers/errors'
import AccountService from '@/services/account/account'
import AssetsService from '@/services/account/assets'

import type { RequestOptions } from '@/services/api.service'
import type {
  AccountType,
  AssetCustomSettings,
  AssetHistoryBalance,
  AssetHistoryBalancesParams,
  AssetHistoryStats,
  AssetHistoryStatsParams,
  AssetInfo,
  AssetInfoAssignTagsParams,
  AssetInfoParams,
  AssetInfoPostParams,
  AssetsCurrencyInfo,
  AssetsTableParams,
  CGCategoriesAsset,
  ClientAsset,
  ClientAssetsTableParams,
  CustomAssetPriceParams,
  UpdateCustomAssetPriceParams,
} from '@/types/services/assets'
import type { DetailedAccount } from '@/types/services/account'

type CGCategoriesAssetWithCategories = CGCategoriesAsset &
  Pick<AssetsCurrencyInfo, 'categories'>

export interface PortfolioState {
  assetsCategories: {
    data: CGCategoriesAssetWithCategories[]
    loading: boolean
  }
  clientAssets: {
    data: ClientAsset[]
    loading: boolean
  }
  accounts: {
    data: Map<string, DetailedAccount>
    loading: boolean
  }
  historyStatsForAsset: string | null
  assetHistoryStats: {
    fiat: {
      data: AssetHistoryStats | null
      loading: boolean
    }
    crypto: {
      data: AssetHistoryStats | null
      loading: boolean
    }
  }
  assetHistoryBalances: {
    data: AssetHistoryBalance[]
    loading: boolean
  }
  assetInfo: {
    loading: boolean
    data: AssetInfo[]
    entityId: string | null
  }
}

let assetsCategoriesAbortController: AbortController | null = null
let cancelSetDataThrottled: (() => void) | null = null

export const usePortfolioStore = defineStore('portfolio', {
  state(): PortfolioState {
    return {
      assetsCategories: {
        data: [],
        loading: false,
      },
      clientAssets: {
        data: [],
        loading: false,
      },
      accounts: {
        data: new Map(),
        loading: true,
      },
      historyStatsForAsset: null,
      assetHistoryStats: {
        fiat: {
          data: null,
          loading: false,
        },
        crypto: {
          data: null,
          loading: false,
        },
      },
      assetHistoryBalances: {
        data: [],
        loading: false,
      },
      assetInfo: {
        loading: false,
        data: [],
        entityId: null,
      },
    }
  },

  actions: {
    async getAssetsCategories(params: AssetsTableParams) {
      if (cancelSetDataThrottled) {
        cancelSetDataThrottled()
        cancelSetDataThrottled = null
      }
      if (assetsCategoriesAbortController) {
        assetsCategoriesAbortController.abort()
      }
      assetsCategoriesAbortController = new AbortController()

      this.$patch((state) => {
        state.assetsCategories.data = []
        state.assetsCategories.loading = true
      })

      let cachedAssets: CGCategoriesAsset[] = []
      let currenciesInfoMap: Record<string, AssetsCurrencyInfo> = {}

      const setData = () => {
        const processed: CGCategoriesAssetWithCategories[] = []
        const remaining: CGCategoriesAsset[] = []

        cachedAssets.forEach((asset) => {
          const currencyInfo = currenciesInfoMap[getCurrencyIdFromAsset(asset)]
          if (currencyInfo) {
            const assetClone = clone(asset) as CGCategoriesAssetWithCategories
            assetClone.categories = currencyInfo.categories
            processed.push(assetClone)
          } else {
            remaining.push(asset)
          }
        })

        if (processed.length > 0) {
          this.assetsCategories.data = [
            ...this.assetsCategories.data,
            ...processed,
          ]
        }

        cachedAssets = remaining
      }

      const setDataThrottled = throttle(setData, 500)
      cancelSetDataThrottled = setDataThrottled.cancel

      const eventHandlers = {
        master_row: (data: CGCategoriesAsset) => {
          cachedAssets.push(data)
        },
        currencies_info: (data: Record<string, AssetsCurrencyInfo>) => {
          currenciesInfoMap = { ...currenciesInfoMap, ...data }
          setDataThrottled()
        },
      }

      const closeHandler = () => {
        setDataThrottled.cancel()
        setData()
        this.assetsCategories.loading = false
        assetsCategoriesAbortController = null
      }

      try {
        await AssetsService.getAssetsStream(
          {
            ...params,
            return_type: 'category_stats',
          },
          ({ event, data }) => eventHandlers[event]?.(data),
          closeHandler,
          assetsCategoriesAbortController
        )
      } catch (err) {
        this.assetsCategories.loading = false
        if (err instanceof DOMException && err.name === 'AbortError') {
          return
        }
        throw new HttpError(err)
      }
    },

    async getClientAssets(
      params: ClientAssetsTableParams,
      options?: RequestOptions
    ) {
      this.$patch((state) => {
        state.clientAssets.data = []
        state.clientAssets.loading = true
      })

      try {
        const response = await AssetsService.getClientAssets(params, options)

        this.$patch((state) => {
          state.clientAssets.data = response.data.data
          state.clientAssets.loading = false
        })
      } catch (err) {
        this.clientAssets.loading = false
        throw new HttpError(err)
      }
    },

    async getAccounts(entityId: string) {
      this.accounts = {
        data: new Map(),
        loading: true,
      }

      try {
        const response = await AccountService.getDetailedAccounts(entityId)

        this.accounts = {
          data: new Map(Object.entries(response.data.data)),
          loading: false,
        }
      } catch (err) {
        this.accounts.loading = false
        throw new HttpError(err)
      }
    },

    async getAssetHistoryStats(
      params: AssetHistoryStatsParams,
      type: 'crypto' | 'fiat'
    ) {
      this.$patch((state) => {
        state.assetHistoryStats[type] = {
          data: null,
          loading: true,
        }
        state.historyStatsForAsset = params.currency_id
      })

      try {
        const response = await AssetsService.getAssetMarketStatsV2(params, type)

        // v2 computes on demand: `data` is the stats object directly (or
        // null on 404/no data), with no cache job to poll for.
        const { data } = response.data

        if (this.historyStatsForAsset == params.currency_id) {
          this.assetHistoryStats[type] = {
            data: data ?? null,
            loading: false,
          }
        }
        return true
      } catch (err) {
        this.assetHistoryStats[type].loading = false
        throw new HttpError(err)
      }
    },

    async getAssetHistoryBalances(params: AssetHistoryBalancesParams) {
      this.assetHistoryBalances = {
        data: [],
        loading: true,
      }

      try {
        const response = await AssetsService.getAssetHistoryBalances(params)
        if (response.data.data.job_triggered) {
          // must call again
          return false
        }

        this.assetHistoryBalances = {
          data: response.data.data.history_balances || [],
          loading: false,
        }

        return true
      } catch (err) {
        this.assetHistoryBalances.loading = false
        throw new HttpError(err)
      }
    },

    async getAssetInfo(params: AssetInfoParams) {
      this.assetInfo = {
        data: [],
        loading: true,
        entityId: null,
      }

      try {
        const response = await AssetsService.getAssetInfo(params)

        this.assetInfo = {
          data: response.data.data,
          loading: false,
          entityId: params.entity_id,
        }
      } catch (err) {
        // Fail closed: keep loading true so empty `data` isn't read as
        // "no tags". Next getAssetInfo() resets it.
        throw new HttpError(err)
      }
    },

    async updateAssetInfo(params: AssetInfoPostParams) {
      try {
        const response = await AssetsService.setAssetInfo(params)
        if (params.tags.some((tag) => !tag.id)) {
          // there was a new tag
          const settingsGeneralStore = useSettingsGeneralStore()
          await settingsGeneralStore.getTags()
        }
        const assetInfo = response.data.data

        const idx = this.assetInfo.data.findIndex(
          (item) => item.id == assetInfo.id
        )
        if (idx != -1) {
          Object.assign(this.assetInfo.data[idx], assetInfo)
        } else {
          this.assetInfo.data.push(assetInfo)
        }

        return assetInfo
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async assignAssetInfoTags(params: AssetInfoAssignTagsParams) {
      try {
        const response = await AssetsService.assignAssetInfoTags(params)

        if (params.tags.some((tag) => !tag.id)) {
          // there was a new tag
          const settingsGeneralStore = useSettingsGeneralStore()
          await settingsGeneralStore.getTags()
        }

        const idx = this.assetInfo.data.findIndex(
          (item) => item.id == params.asset_info_id
        )

        if (idx != -1) {
          const otherTags = this.assetInfo.data[idx].asset_info_tags.filter(
            (tag) =>
              tag.account_id != params.account_id ||
              tag.source_id != params.source_id ||
              tag.asset_type != params.asset_type
          )

          Object.assign(this.assetInfo.data[idx], {
            asset_info_tags: [...otherTags, ...response.data.data],
          })
        }
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async createCustomPrice(params: CustomAssetPriceParams) {
      try {
        await AssetsService.createCustomAssetPrice(params)
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async closeCustomPrice(params: {
      customSettings: AssetCustomSettings
      closeToDate: Date
    }) {
      const { customSettings, closeToDate } = params
      const userStore = useUserStore()
      const entity = userStore.entity

      const { date_range, ...originalSettings } = customSettings

      try {
        await AssetsService.updateCustomAssetPrice({
          ...originalSettings,
          date_from: date_range.from,
          date_to: userStore.utcCloseFromDate(closeToDate, entity),
        })
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async updateCustomPrice(params: UpdateCustomAssetPriceParams) {
      try {
        await AssetsService.updateCustomAssetPrice(params)
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async removeCustomPrice(custom_settings_id: string) {
      try {
        await AssetsService.removeCustomAssetPrice(custom_settings_id)
      } catch (err) {
        throw new HttpError(err)
      }
    },
  },

  getters: {
    getAccountById(state) {
      return (accountId: string): DetailedAccount | null => {
        const account = state.accounts.data.get(accountId) || null
        if (!state.accounts.loading && !account) {
          Sentry.captureException(
            new Error('Account not found in getAccountById'),
            {
              extra: {
                accountId,
                accountsCount: state.accounts.data.size,
                accountIds: Array.from(state.accounts.data.keys()),
                loading: state.accounts.loading,
              },
            }
          )
        }
        return account
      }
    },

    getAccountsMapByTypeId(state) {
      const map: Record<AccountType, Record<string, DetailedAccount>> = {
        exchange: {},
        blockchain: {},
        ico: {},
        manual_entry: {},
      }

      for (const account of state.accounts.data.values()) {
        map[account.type][account[account.type]!.id] = account
      }

      return map
    },

    getAccountByICOId() {
      return (icoId: string): DetailedAccount | null => {
        return this.getAccountsMapByTypeId.ico[icoId] || null
      }
    },

    getAccountByBlockchainId() {
      return (blockchainId: string): DetailedAccount | null => {
        return this.getAccountsMapByTypeId.blockchain[blockchainId] || null
      }
    },

    getAccountByExchangeId() {
      return (exchangeId: string): DetailedAccount | null => {
        return this.getAccountsMapByTypeId.exchange[exchangeId] || null
      }
    },

    assetInfoMap(state): Record<string, AssetInfo> {
      const map: Record<string, AssetInfo> = {}
      state.assetInfo.data.forEach((item) => {
        const key = item.currency_id || item.ico_id
        if (key) {
          map[key] = item
        }
      })
      return map
    },

    findAssetInfos() {
      return (params: {
        currency_id?: string
        ico_id?: string
        account_id?: string | null
        asset_type?: string | null
      }): AssetInfo[] => {
        const key = params.currency_id || params.ico_id

        if (!key) {
          return []
        }

        const assetInfo = this.assetInfoMap[key]
        if (!assetInfo) {
          return []
        }

        if (!params.account_id || !params.asset_type) {
          return [assetInfo]
        }

        const hasMatchingTag = assetInfo.asset_info_tags.some((tag) => {
          const accountMatch = params.account_id
            ? tag.account_id == params.account_id
            : true

          const assetTypeMatch = params.asset_type
            ? tag.asset_type == params.asset_type
            : true

          return accountMatch && assetTypeMatch
        })

        return hasMatchingTag ? [assetInfo] : []
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePortfolioStore, import.meta.hot))
}
